import type { ComponentProps } from 'react';

import { tv } from '../../lib/tv';
import { Skeleton as SkeletonPrimitive } from '../ui/skeleton';
import { FeedbackKeyframes } from './keyframes';

export type SkeletonShape = 'line' | 'circle' | 'rectangle';

/**
 * Figma Skeleton (82:9): a bg/muted bone, clipped. Line 200×12 radius/full
 * (text), Circle 40 radius/full (avatars, icons), Rectangle 200×112
 * radius/md (media, charts) - resize freely. Shimmer: a 60%-wide white
 * highlight (0 → 55% → 0 alpha) sweeping left → right, 1.4s linear loop;
 * under reduced motion it isn't drawn (static bones).
 */
const skeleton = tv({
  slots: {
    root: 'block max-w-full shrink-0',
    shimmer: [
      'pointer-events-none absolute inset-y-0 left-0 w-[60%]',
      // Figma: white at 0 → 55% → 0 alpha (plain sRGB stops, as Figma blends).
      'bg-[linear-gradient(90deg,rgb(255_255_255/0),rgb(255_255_255/0.55),rgb(255_255_255/0))]',
      'motion-safe:animate-[rp-skeleton-shimmer_1.4s_linear_infinite] motion-reduce:hidden',
    ],
  },
  variants: {
    shape: {
      line: { root: 'h-3 w-full rounded-full' },
      circle: { root: 'size-10 rounded-full' },
      rectangle: { root: 'h-28 w-full rounded-md' },
    },
  },
  defaultVariants: { shape: 'line' },
});

export type SkeletonProps = Omit<ComponentProps<'div'>, 'children'> & {
  /** Figma "Shape": Line (text) · Circle (avatars, icons) · Rectangle (media, charts). */
  shape?: SkeletonShape;
  /** Figma "Shimmer": the sweeping highlight. Off by default; static under reduced motion. */
  shimmer?: boolean;
};

/**
 * Loading placeholder bone (Figma: ↳ Skeleton ↳ Skeleton). Size it with
 * `className` to the content it stands in for. Decorative (`aria-hidden`):
 * mark the region that is loading `aria-busy="true"` and give it one
 * screen-reader status ("Loading reels…"). Don't show skeletons for waits
 * under 300ms.
 */
export function Skeleton({ shape, shimmer = false, className, ...props }: SkeletonProps) {
  const styles = skeleton({ shape });
  return (
    <SkeletonPrimitive
      data-shape={shape ?? 'line'}
      className={styles.root({ className })}
      {...props}
    >
      {shimmer && (
        <>
          <FeedbackKeyframes />
          <span data-slot="skeleton-shimmer" className={styles.shimmer()} />
        </>
      )}
    </SkeletonPrimitive>
  );
}

export type SkeletonPatternType =
  'card' | 'table-row' | 'list-item' | 'stat-tile' | 'text-block';

/**
 * Figma Skeleton Pattern (82:77): placeholders with the real components'
 * geometry. Card 320 (Card: bg/surface, 1px border/subtle, radius/lg; 180
 * media; content p-4 gap 10) · Table Row 56 (px-4 gap-6, border-b) · List
 * Item 64 (px-4 gap-3) · Stat Tile 260 (p-5 gap-3, bordered card) · Text
 * Block 400 (gap 10; lines 55 / 100 / 95 / 70%).
 */
const pattern = tv({
  base: 'flex max-w-full',
  variants: {
    type: {
      card: 'bg-bg-surface border-border-subtle w-80 flex-col overflow-hidden rounded-lg border',
      'table-row':
        'bg-bg-surface border-border-subtle h-14 w-full items-center gap-6 border-b px-4',
      'list-item': 'bg-bg-surface h-16 w-90 items-center gap-3 px-4',
      'stat-tile':
        'bg-bg-surface border-border-subtle w-65 flex-col gap-3 rounded-lg border p-5',
      'text-block': 'w-100 flex-col gap-2.5',
    },
  },
});

export type SkeletonPatternProps = Omit<ComponentProps<'div'>, 'children'> & {
  /** Figma "Type": the component the placeholder stands in for. */
  type: SkeletonPatternType;
  /** Shimmer on every bone. */
  shimmer?: boolean;
};

/**
 * A composed loading placeholder that mirrors a real component so nothing
 * jumps when content arrives (Figma: ↳ Skeleton ↳ Skeleton Pattern). Keep
 * real chrome (sidebar, headers, tabs) visible - skeleton only the data -
 * and swap to real content in one pass. Decorative (`aria-hidden`).
 */
export function SkeletonPattern({
  type,
  shimmer = false,
  className,
  ...props
}: SkeletonPatternProps) {
  const bone = (shape: SkeletonShape, cls: string) => (
    <Skeleton shape={shape} shimmer={shimmer} className={cls} />
  );
  return (
    <div
      aria-hidden
      data-slot="skeleton-pattern"
      data-type={type}
      className={pattern({ type, className })}
      {...props}
    >
      {type === 'card' && (
        <>
          {bone('rectangle', 'h-45 rounded-none')}
          <div className="flex flex-col gap-2.5 p-4">
            {bone('line', 'h-2 w-24')}
            {bone('line', 'h-3.5 w-50')}
            {bone('line', 'h-2.5 w-68')}
            {bone('line', 'h-2.5 w-45')}
            <div className="flex items-center gap-2 pt-1.5">
              {bone('circle', 'size-6')}
              {bone('line', 'h-2.5 w-25')}
              <span className="flex-1" />
              {bone('line', 'h-4.5 w-12')}
            </div>
          </div>
        </>
      )}
      {type === 'table-row' && (
        <>
          {bone('rectangle', 'size-4.5')}
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            {bone('line', 'h-3 w-45')}
            {bone('line', 'h-2 w-27.5')}
          </div>
          <div className="flex w-44 shrink-0 items-center gap-2">
            {bone('circle', 'size-6')}
            {bone('line', 'h-2.5 w-27.5')}
          </div>
          <div className="flex w-29 shrink-0">{bone('line', 'h-5 w-16')}</div>
          <div className="flex w-24 shrink-0 justify-end">{bone('line', 'h-3 w-14')}</div>
          {bone('rectangle', 'size-6')}
        </>
      )}
      {type === 'list-item' && (
        <>
          {bone('circle', 'size-10')}
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            {bone('line', 'h-3 w-40')}
            {bone('line', 'h-2.5 w-55')}
          </div>
        </>
      )}
      {type === 'stat-tile' && (
        <>
          {bone('line', 'h-2.5 w-27.5')}
          {bone('line', 'h-7 w-22.5')}
          {bone('line', 'h-2 w-17.5')}
          {bone('rectangle', 'h-9')}
        </>
      )}
      {type === 'text-block' && (
        <>
          {bone('line', 'h-5 w-[55%]')}
          {bone('line', 'w-full')}
          {bone('line', 'w-[95%]')}
          {bone('line', 'w-[70%]')}
        </>
      )}
    </div>
  );
}
